import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Plus, Edit, Trash2, Eye, Layout, ArrowLeft, Save, Loader2,
  Monitor, Tablet, Smartphone, ExternalLink, Undo2, Redo2, Home
} from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import BlockPalette from '@/components/builder/BlockPalette';
import BlockSettings from '@/components/builder/BlockSettings';
import EditorCanvas from '@/components/builder/EditorCanvas';

export default function PageBuilder() {
  // ====== State ======
  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editor state
  const [editorMode, setEditorMode] = useState(false); // false = page list, true = editor
  const [currentPage, setCurrentPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [pageSettings, setPageSettings] = useState({ title: '', slug: '', page_type: 'custom', is_published: false, seo_title: '', seo_description: '' });
  const [deviceWidth, setDeviceWidth] = useState('desktop');
  const [sidebarTab, setSidebarTab] = useState('blocks');

  // Undo/Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);

  // New page dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newPageForm, setNewPageForm] = useState({ title: '', slug: '', page_type: 'custom' });
  const [creating, setCreating] = useState(false);

  const { toast } = useToast();

  // ====== Data Fetching ======
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pagesRes, templatesRes, storeRes] = await Promise.all([
        api.get('/tenant/pages'),
        api.get('/tenant/block-templates'),
        api.get('/tenant/store')
      ]);
      setPages(pagesRes.data.pages || []);
      setTemplates(templatesRes.data.templates || []);
      setStore(storeRes.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ====== History (Undo/Redo) ======
  useEffect(() => {
    if (isUndoRedo.current) {
      isUndoRedo.current = false;
      return;
    }
    if (!editorMode) return;
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(blocks)));
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [blocks, editorMode]);

  const undo = () => {
    if (historyIndex <= 0) return;
    isUndoRedo.current = true;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setBlocks(JSON.parse(JSON.stringify(history[newIndex])));
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    isUndoRedo.current = true;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    setBlocks(JSON.parse(JSON.stringify(history[newIndex])));
  };

  // ====== Page CRUD ======
  const openEditor = (page) => {
    setCurrentPage(page);
    setBlocks(page.blocks || []);
    setPageSettings({
      title: page.title, slug: page.slug, page_type: page.page_type || 'custom',
      is_published: page.is_published, seo_title: page.seo_title || '', seo_description: page.seo_description || ''
    });
    setSelectedBlockId(null);
    setHistory([]);
    setHistoryIndex(-1);
    setEditorMode(true);
    setSidebarTab('blocks');
  };

  const createNewPage = async () => {
    if (!newPageForm.title || !newPageForm.slug) {
      toast({ title: 'Error', description: 'Judul dan slug wajib diisi', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/tenant/pages', {
        ...newPageForm,
        blocks: [],
        is_published: false
      });
      setShowNewDialog(false);
      setNewPageForm({ title: '', slug: '', page_type: 'custom' });
      toast({ title: 'Berhasil', description: 'Halaman dibuat' });
      openEditor(data.page);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal membuat halaman', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const savePage = async () => {
    if (!currentPage) return;
    setSaving(true);
    try {
      await api.put(`/tenant/pages/${currentPage.id}`, {
        ...pageSettings,
        blocks: blocks.map((b, i) => ({ ...b, order: i }))
      });
      toast({ title: 'Tersimpan!', description: 'Halaman berhasil disimpan' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal menyimpan', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Yakin ingin menghapus halaman ini?')) return;
    try {
      await api.delete(`/tenant/pages/${pageId}`);
      toast({ title: 'Berhasil', description: 'Halaman dihapus' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal menghapus', variant: 'destructive' });
    }
  };

  const exitEditor = () => {
    setEditorMode(false);
    setCurrentPage(null);
    setSelectedBlockId(null);
    fetchData();
  };

  // ====== Block Operations ======
  const addBlock = (newBlock) => {
    newBlock.order = blocks.length;
    setBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
    setSidebarTab('settings');
  };

  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.id !== blockId));
    if (selectedBlockId === blockId) setSelectedBlockId(null);
  };

  const duplicateBlock = (blockId) => {
    const idx = blocks.findIndex(b => b.id === blockId);
    if (idx < 0) return;
    const clone = {
      ...JSON.parse(JSON.stringify(blocks[idx])),
      id: Math.random().toString(36).substr(2, 8)
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    setBlocks(newBlocks.map((b, i) => ({ ...b, order: i })));
    setSelectedBlockId(clone.id);
  };

  const updateBlockContent = (blockId, content) => {
    setBlocks(blocks.map(b => b.id === blockId ? { ...b, content } : b));
  };

  const updateBlockStyle = (blockId, style) => {
    setBlocks(blocks.map(b => b.id === blockId ? { ...b, style } : b));
  };

  const toggleBlockVisibility = (blockId, visible) => {
    setBlocks(blocks.map(b => b.id === blockId ? { ...b, is_visible: visible } : b));
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId) || null;
  const subdomain = store?.subdomain || '';
  const primaryColor = store?.primary_color || '#2D6CDF';

  // ====== Create Homepage Helper ======
  const createHomePage = async () => {
    try {
      const defaultBlocks = [
        { id: 'h1', type: 'hero', content: { title: store?.name || 'Selamat Datang', subtitle: store?.description || 'Toko online terbaik untuk Anda', button_text: 'Belanja Sekarang', button_link: '#products', background_color: primaryColor, background_image: '' }, style: {}, order: 0, is_visible: true },
        { id: 'p1', type: 'products', content: { title: 'Produk Unggulan', count: 8, category: '', columns: 4 }, style: { paddingTop: '40px', paddingBottom: '40px' }, order: 1, is_visible: true },
        { id: 'f1', type: 'features', content: { title: 'Mengapa Memilih Kami', items: [{ icon: 'truck', title: 'Gratis Ongkir', description: 'Untuk pembelian di atas Rp 100.000' }, { icon: 'shield', title: 'Garansi 100%', description: 'Uang kembali jika tidak puas' }, { icon: 'headphones', title: 'Support 24/7', description: 'Siap membantu kapan saja' }] }, style: { paddingTop: '40px', paddingBottom: '40px' }, order: 2, is_visible: true },
        { id: 't1', type: 'testimonials', content: { title: 'Apa Kata Mereka', items: [{ name: 'John Doe', text: 'Produk sangat berkualitas!', rating: 5 }, { name: 'Jane Smith', text: 'Pengiriman cepat, recommended!', rating: 5 }] }, style: { paddingTop: '40px', paddingBottom: '40px' }, order: 3, is_visible: true },
        { id: 'c1', type: 'cta', content: { title: 'Siap untuk Memulai?', subtitle: 'Daftar sekarang dan dapatkan diskon 10%', button_text: 'Mulai Belanja', button_link: '#products' }, style: {}, order: 4, is_visible: true }
      ];
      const { data } = await api.post('/tenant/pages', {
        title: 'Homepage', slug: 'home', page_type: 'home',
        blocks: defaultBlocks, is_published: true
      });
      toast({ title: 'Berhasil', description: 'Homepage dibuat dengan template default' });
      openEditor(data.page);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal membuat homepage', variant: 'destructive' });
    }
  };

  // ====== RENDER - Loading ======
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  // ====== RENDER - Editor Mode ======
  if (editorMode && currentPage) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="page-builder-editor">
        {/* Top Toolbar */}
        <div className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={exitEditor} className="gap-1.5">
              <ArrowLeft size={16} /> Kembali
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <Input
                value={pageSettings.title}
                onChange={e => setPageSettings({ ...pageSettings, title: e.target.value })}
                className="h-8 w-48 text-sm font-semibold border-transparent hover:border-slate-300 focus:border-blue-500"
              />
              {pageSettings.page_type === 'home' && <Badge className="bg-blue-100 text-blue-700"><Home size={10} className="mr-1" /> Homepage</Badge>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Undo/Redo */}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={undo} disabled={historyIndex <= 0} title="Undo">
              <Undo2 size={15} />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={redo} disabled={historyIndex >= history.length - 1} title="Redo">
              <Redo2 size={15} />
            </Button>
            <div className="h-6 w-px bg-slate-200" />

            {/* Device Preview */}
            <div className="flex items-center border rounded-md">
              {[
                { key: 'desktop', icon: Monitor, label: 'Desktop' },
                { key: 'tablet', icon: Tablet, label: 'Tablet' },
                { key: 'mobile', icon: Smartphone, label: 'Mobile' }
              ].map(d => (
                <Button
                  key={d.key}
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 rounded-none first:rounded-l-md last:rounded-r-md ${deviceWidth === d.key ? 'bg-slate-100 text-blue-600' : ''}`}
                  onClick={() => setDeviceWidth(d.key)}
                  title={d.label}
                >
                  <d.icon size={14} />
                </Button>
              ))}
            </div>
            <div className="h-6 w-px bg-slate-200" />

            {/* Publish Toggle */}
            <div className="flex items-center gap-1.5">
              <Switch
                checked={pageSettings.is_published}
                onCheckedChange={v => setPageSettings({ ...pageSettings, is_published: v })}
              />
              <span className="text-xs text-slate-500">{pageSettings.is_published ? 'Published' : 'Draft'}</span>
            </div>

            {/* Preview */}
            {subdomain && pageSettings.slug && (
              <Button variant="outline" size="sm" asChild>
                <a href={`https://${subdomain}.tokomoe.com/page/${pageSettings.slug}?preview=1`} target="_blank" rel="noopener noreferrer" className="gap-1.5">
                  <ExternalLink size={13} /> Preview
                </a>
              </Button>
            )}

            {/* Save */}
            <Button size="sm" onClick={savePage} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#0D47E6] gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Simpan
            </Button>
          </div>
        </div>

        {/* Editor Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar */}
          <div className="w-80 border-r border-slate-200 bg-white flex flex-col shrink-0">
            <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="flex flex-col h-full">
              <TabsList className="w-full rounded-none border-b h-10 bg-slate-50 shrink-0">
                <TabsTrigger value="blocks" className="flex-1 text-xs">🧱 Blocks</TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 text-xs">⚙️ Settings</TabsTrigger>
                <TabsTrigger value="page" className="flex-1 text-xs">📄 Page</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto p-3">
                <TabsContent value="blocks" className="mt-0">
                  <BlockPalette onAddBlock={addBlock} templates={templates} />
                </TabsContent>

                <TabsContent value="settings" className="mt-0">
                  <BlockSettings
                    block={selectedBlock}
                    onUpdateContent={updateBlockContent}
                    onUpdateStyle={updateBlockStyle}
                    onToggleVisibility={toggleBlockVisibility}
                  />
                </TabsContent>

                <TabsContent value="page" className="mt-0 space-y-4">
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pengaturan Halaman</p>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Judul</Label>
                      <Input value={pageSettings.title} onChange={e => setPageSettings({ ...pageSettings, title: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Slug (URL)</Label>
                      <Input
                        value={pageSettings.slug}
                        onChange={e => setPageSettings({ ...pageSettings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                      />
                      {subdomain && <p className="text-[10px] text-slate-400">{subdomain}.tokomoe.com/page/{pageSettings.slug}</p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">SEO Title</Label>
                      <Input value={pageSettings.seo_title} onChange={e => setPageSettings({ ...pageSettings, seo_title: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">SEO Description</Label>
                      <Input value={pageSettings.seo_description} onChange={e => setPageSettings({ ...pageSettings, seo_description: e.target.value })} />
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>

          {/* Canvas */}
          <EditorCanvas
            blocks={blocks}
            setBlocks={setBlocks}
            selectedBlockId={selectedBlockId}
            onSelectBlock={(id) => { setSelectedBlockId(id); if (id) setSidebarTab('settings'); }}
            onDeleteBlock={deleteBlock}
            onDuplicateBlock={duplicateBlock}
            onToggleVisibility={toggleBlockVisibility}
            subdomain={subdomain}
            primaryColor={primaryColor}
            deviceWidth={deviceWidth}
          />
        </div>
      </div>
    );
  }

  // ====== RENDER - Page List ======
  const homePage = pages.find(p => p.page_type === 'home');

  return (
    <div className="space-y-6" data-testid="page-builder">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Page Builder</h1>
          <p className="text-sm text-slate-500 mt-1">Buat & edit halaman toko seperti Elementor</p>
        </div>
        <div className="flex gap-2">
          {!homePage && (
            <Button variant="outline" onClick={createHomePage} className="gap-1.5">
              <Home size={15} /> Buat Homepage
            </Button>
          )}
          <Button onClick={() => { setNewPageForm({ title: '', slug: '', page_type: 'custom' }); setShowNewDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#0D47E6] gap-1.5" data-testid="create-page-btn">
            <Plus size={15} /> Buat Halaman
          </Button>
        </div>
      </div>

      {/* Homepage Card (pinned) */}
      {homePage && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home size={18} className="text-blue-600" />
                <CardTitle className="text-lg text-blue-900">{homePage.title}</CardTitle>
              </div>
              <Badge className="bg-blue-100 text-blue-700">Homepage</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-blue-700/70 mb-3">
              <Layout size={14} /> {homePage.blocks?.length || 0} blocks
              <span className="mx-1">•</span>
              {homePage.is_published ? '✅ Published' : '📝 Draft'}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => openEditor(homePage)} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
                <Edit size={13} /> Edit Homepage
              </Button>
              {subdomain && (
                <Button variant="outline" size="sm" asChild className="text-blue-700 border-blue-300">
                  <a href={`https://${subdomain}.tokomoe.com`} target="_blank" rel="noopener noreferrer" className="gap-1.5">
                    <Eye size={13} /> Lihat Toko
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Pages Grid */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Halaman Custom</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.filter(p => p.page_type !== 'home').map(page => (
            <Card key={page.id} className="hover:shadow-md transition-shadow" data-testid={`page-card-${page.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{page.title}</CardTitle>
                  <Badge variant={page.is_published ? 'default' : 'secondary'} className="text-xs">
                    {page.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400">/{page.slug}</p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                  <Layout size={14} /> {page.blocks?.length || 0} blocks
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => openEditor(page)}>
                    <Edit size={13} /> Edit
                  </Button>
                  {subdomain && page.is_published && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`https://${subdomain}.tokomoe.com/page/${page.slug}`} target="_blank" rel="noopener noreferrer"><Eye size={13} /></a>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => deletePage(page.id)}>
                    <Trash2 size={13} className="text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {pages.filter(p => p.page_type !== 'home').length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <div className="text-4xl mb-3">📄</div>
                <p className="text-slate-500">Belum ada halaman custom. Klik "Buat Halaman" untuk membuat.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Page Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Buat Halaman Baru</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Judul Halaman</Label>
              <Input
                value={newPageForm.title}
                onChange={e => setNewPageForm({
                  ...newPageForm,
                  title: e.target.value,
                  slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                })}
                placeholder="Contoh: Tentang Kami"
              />
            </div>
            <div>
              <Label>Slug (URL)</Label>
              <Input
                value={newPageForm.slug}
                onChange={e => setNewPageForm({ ...newPageForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder="tentang-kami"
              />
              {subdomain && <p className="text-xs text-slate-400 mt-1">{subdomain}.tokomoe.com/page/{newPageForm.slug || '...'}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Batal</Button>
            <Button onClick={createNewPage} disabled={creating} className="bg-[#2D6CDF] hover:bg-[#0D47E6]">
              {creating && <Loader2 size={16} className="animate-spin mr-1" />}
              {creating ? 'Membuat...' : 'Buat & Edit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
